import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchTeamsDDL,
  selectTeamsState,
} from '@/redux-toolkit/slice/teamsSlice';
import type { CompanyTeam } from '@/apis/settings';
import type { TextTemplateChunk } from '@/proto/logiblocks/logiblocks';
import useToken from '@/common/helper/useToken';
import type { AppDispatch } from '@/redux-toolkit/store';
import styles from '../NodeCustomization.module.css';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { TemplatedTextArea } from '@/pages/Logiblocks/variables/TemplatedTextArea/TemplatedTextArea';
import { FieldErrorLabel, FieldLabel } from '../shared';
import { TeamsNotificationFields } from './TeamsNotificationNodeInfo';
import { BrackedAddIcon } from '@/assets/icons/icons';
import { Flex, Form } from 'antd';
import { NotificationFrequency } from '@/pages/Shipments/PerShipmentNotification/components';
import dayjs from 'dayjs';
import TeamsDropdownLB from '../shared/TeamsDropdownLB';
import { useTeamGroups } from '@/hooks/useTeamGroups';

type FrequencyFormValues = {
  frequency?: string;
  day?: string;
  time_1?: dayjs.Dayjs | null;
  time_2?: dayjs.Dayjs | null;
};
export const TeamsNotificationCustomization: CustomizationComponent = ({
  nodeId,
  nodeData,
  onChange,
  isReadOnly = false,
  validationErrors,
}) => {
  const recipientTeamsError = validationErrors?.find(
    (e) => e.field === TeamsNotificationFields.RECIPIENT_TEAMS
  );
  const messageTemplateError = validationErrors?.find(
    (e) => e.field === TeamsNotificationFields.MESSAGE_TEMPLATE
  );
  const dispatch = useDispatch<AppDispatch>();
  const [selectedFrequencyOption, setSelectedFrequencyOption] =
    useState('REALTIME');
  const { getToken } = useToken();
  const teamsState = useSelector(selectTeamsState);
  // Team groups expand to their teams in the recipients picker.
  const teamGroups = useTeamGroups(teamsState.list.map((team) => team.id));
  const [selectedFrequency, setSelectedFrequency] = useState('REALTIME');
  const [selectedTeams, setSelectedTeams] = useState<CompanyTeam[]>([]);
  const messageChunks = nodeData.teams_notification?.message_chunks ?? [];
  const [form] = Form.useForm();

  const triggerPickerRef = useRef<(() => void) | null>(null);

  // Fetch teams on mount if not already loaded
  useEffect(() => {
    const token = getToken();
    if (token && teamsState.list.length === 0) {
      // Fire and forget: the thunk settles with a rejected action, never throws.
      const _ = dispatch(fetchTeamsDDL(token));
    }
  }, [dispatch, getToken, teamsState.list.length]);

  // Helper: patch all local state + form fields from a given nodeData snapshot.
  const patchFromNodeData = useCallback(
    (data: typeof nodeData, teamsList: CompanyTeam[]) => {
      const teamIds = data.teams_notification?.recipient_team_ids || [];
      const teams = teamIds
        .map((id) => teamsList.find((team) => team.id === id))
        .filter((team): team is CompanyTeam => team !== undefined);
      setSelectedTeams(teams);

      const freq = data.teams_notification?.frequency || 'REALTIME';
      const day = data.teams_notification?.day || '';
      const t1 = data.teams_notification?.time1 || '';
      const t2 = data.teams_notification?.time2 || '';

      setSelectedFrequency(freq);
      setSelectedFrequencyOption(freq !== 'REALTIME' ? 'digest' : 'REALTIME');

      form.setFieldsValue({
        frequency: freq,
        day: day,
        time_1: t1 ? dayjs(t1, 'h:mm a') : null,
        time_2: t2 ? dayjs(t2, 'h:mm a') : null,
      });
    },
    [form]
  );

  // A ref to keep track of the nodeId we have already patched/loaded.
  const loadedNodeIdRef = useRef<string | null>(null);

  // Run when the panel opens or the user switches to a different node,
  // waiting for the teams list to be loaded before patching.
  useEffect(() => {
    if (loadedNodeIdRef.current !== nodeId && teamsState.list.length > 0) {
      patchFromNodeData(nodeData, teamsState.list);
      loadedNodeIdRef.current = nodeId;
    }
  }, [nodeId, teamsState.list, patchFromNodeData, nodeData]);

  const getFormFrequencyValues = (allValues: FrequencyFormValues) => {
    const freq = allValues.frequency || 'REALTIME';
    if (freq === 'REALTIME') {
      return {
        frequency: 'REALTIME',
        day: '',
        time1: '',
        time2: '',
      };
    }
    const day = allValues.day || '';
    const time_1 = allValues.time_1 ? allValues.time_1.format('h:mm a') : '';
    const time_2 = allValues.time_2 ? allValues.time_2.format('h:mm a') : '';
    return {
      frequency: freq,
      day,
      time1: time_1,
      time2: time_2,
    };
  };

  const getFormFrequencyValuesFromForm = () => {
    const vals = form.getFieldsValue();
    return getFormFrequencyValues(vals);
  };

  const handleTeamsChange = (teams: CompanyTeam[]) => {
    setSelectedTeams(teams);
    const freqVals = getFormFrequencyValuesFromForm();
    onChange({
      teams_notification: {
        recipient_team_ids: teams.map((team) => team.id),
        message_chunks: messageChunks,
        ...freqVals,
      },
    });
  };

  const handleMessageChange = (newChunks: TextTemplateChunk[]) => {
    const freqVals = getFormFrequencyValuesFromForm();
    onChange({
      teams_notification: {
        recipient_team_ids: selectedTeams.map((team) => team.id),
        message_chunks: newChunks,
        ...freqVals,
      },
    });
  };

  const handleSelectedFrequencyChange = (newFreq: string) => {
    setSelectedFrequency(newFreq);
    const nextFormValues: FrequencyFormValues = {
      frequency: newFreq,
      day: '',
      time_1: null,
      time_2: null,
    };
    form.setFieldsValue(nextFormValues);
    const freqVals = getFormFrequencyValues(nextFormValues);
    onChange({
      teams_notification: {
        recipient_team_ids: selectedTeams.map((team) => team.id),
        message_chunks: messageChunks,
        ...freqVals,
      },
    });
  };

  const handleValuesChange = (
    _changedValues: FrequencyFormValues,
    allValues: FrequencyFormValues
  ) => {
    const freqVals = getFormFrequencyValues(allValues);
    onChange({
      teams_notification: {
        recipient_team_ids: selectedTeams.map((team) => team.id),
        message_chunks: messageChunks,
        ...freqVals,
      },
    });
  };
  const [teamsSearch, setTeamsSearch] = useState<string>('');
  const teamsLoading = teamsState.loading;

  const filteredTeamsList = useMemo(() => {
    if (!teamsSearch) return teamsState.list;
    const query = teamsSearch.toLowerCase();
    return teamsState.list.filter((team) =>
      team.name?.toLowerCase().includes(query)
    );
  }, [teamsState.list, teamsSearch]);

  return (
    <Form form={form} layout='vertical' onValuesChange={handleValuesChange}>
      <div className={styles.customizationSection}>
        <FieldLabel required className={styles.sectionLabel}>
          Recipients
        </FieldLabel>
        {teamsLoading ? (
          <div>Loading teams...</div>
        ) : teamsState.list.length === 0 ? (
          <div>No teams available</div>
        ) : (
          <TeamsDropdownLB
            teamsOptions={filteredTeamsList}
            selectedTeams={selectedTeams}
            onChange={handleTeamsChange}
            disabled={isReadOnly}
            provider='teams'
            teamsSearch={teamsSearch}
            onSearch={setTeamsSearch}
            teamGroups={teamGroups}
            groupTeamsOptions={teamsState.list}
          />
        )}
        <FieldErrorLabel error={recipientTeamsError} />
      </div>

      <div className={styles.customizationSection}>
        <Flex align='center' gap={4} style={{ marginBottom: '8px' }}>
          <FieldLabel
            required
            className={styles.sectionLabel}
            style={{ marginBottom: 0 }}
          >
            Message
          </FieldLabel>
          {!isReadOnly && (
            <button
              type='button'
              className={styles.iconButton}
              onClick={() => triggerPickerRef.current?.()}
              aria-label='Insert variable'
            >
              <BrackedAddIcon />
            </button>
          )}
        </Flex>
        <TemplatedTextArea
          nodeId={nodeId}
          chunks={messageChunks}
          onChange={handleMessageChange}
          placeholder='Enter your message here...'
          disabled={isReadOnly}
          pickerAsModal
          onTriggerPickerRef={(fn) => {
            triggerPickerRef.current = fn;
          }}
        />
        <FieldErrorLabel error={messageTemplateError} />
      </div>

      <div className={styles.customizationSection}>
        <NotificationFrequency
          form={form}
          selectedFrequencyOption={selectedFrequencyOption}
          setSelectedFrequencyOption={setSelectedFrequencyOption}
          selectedFrequency={selectedFrequency}
          setSelectedFrequency={handleSelectedFrequencyChange}
          notificationMethod={null}
          alertType={null}
          disableDigestFromTeamsNode={true}
        />
      </div>
    </Form>
  );
};
